import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { AppState } from '../../../../reducers';
import { StartFlowGlobalStore } from '../../../../start-flow/store/start-flow-global.store';
import { DictionaryGlobalStore } from '../../../../dictionary/store/dictionary-global.store';
import { AccountGlobalStore } from '../../../../account/store/account-global.store';
import { CreateAccountStore } from '../../../../account/store/create-account.store';
import { Language } from '../../../../dictionary/models/language.model';
import { Locale } from '../../../../dictionary/models/locale.model';
import { RoutesHelper } from '../../../../core/helpers/routes.helper';
import { CoreRoutes } from '../../../../core/routes/core.routes';
import {
  authInvitationHashSelector,
  authUserSelector,
} from '../../../../auth/store/auth.selectors';
import { PrimaryResidence } from '../../../../account/models/primary-residence.model';
import { Phone } from '../../../../account/models/phone.model';
import { Country } from '../../../../shared/sd-forms/models/country.model';
import { Es01ClientGlobalStore } from '../../store/es01-client-global.store';

/**
 * SPD-4426 — ES-01 auto-create flow for federated users.
 *
 * Registers a new-account navigation override on the shared `StartFlowGlobalStore`.
 * When the account lookup returns 404 (new account) and the token is federated,
 * this builds the create payload from the token + ES-01 client defaults and
 * creates the account automatically (reusing the shared `CreateAccountStore`),
 * then resumes the normal start flow. If the user is not federated or required
 * information is missing, it returns `false` so the default manual create form runs.
 *
 * Mirrors `TdStartFlowStore`, minus TD's role/forbidden gate (ES-01 auto-creates
 * for all federated users).
 */
@Injectable()
export class Es01StartFlowStore {
  private readonly startFlowStore = inject(StartFlowGlobalStore);
  private readonly ngrxStore = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);
  private readonly dictionaryGlobalStore = inject(DictionaryGlobalStore);
  private readonly accountGlobalStore = inject(AccountGlobalStore);
  private readonly createAccountStore = inject(CreateAccountStore);
  private readonly es01ClientStore = inject(Es01ClientGlobalStore);

  initialize(): void {
    this.startFlowStore.setNewAccountNavigationOverride(() =>
      this.navigateNewAccount()
    );
  }

  /**
   * ES-01-specific new-account navigation. Returns `true` when handled
   * (auto-create attempted), `false` to let the default create-form path run.
   */
  private navigateNewAccount(): boolean {
    const authUser = this.ngrxStore.selectSignal(authUserSelector)();
    const brandCode = this.es01ClientStore.currentBrandCode();

    const isFederated = authUser?.federationId?.length > 0;
    if (!isFederated) {
      return false;
    }

    // Completeness gate — only auto-create when we have everything we need.
    const hasRequiredInfo =
      !!authUser?.email &&
      !!authUser?.givenName &&
      !!authUser?.familyName &&
      !!brandCode;
    if (!hasRequiredInfo) {
      return false;
    }

    // If we can't determine a valid country (collection failed to load, no default
    // region configured, or an unrecognized country code), we cannot auto-create —
    // fall back to the manual create-account view.
    const countryCode = this.resolveEs01CountryCode();
    if (!countryCode) {
      return false;
    }

    const primaryResidenceSelected = new PrimaryResidence();
    primaryResidenceSelected.country =
      Country.getCountryByCountryCode(countryCode).countryName;

    const locale = this.resolveEs01Locale(countryCode);

    // Zeroed placeholder phone; invitation phone (if any) takes priority in dto().
    const placeholderPhone = new Phone(true, 0, 0, true, false);

    const queryParams = RoutesHelper.getParams(false);

    this.createAccountStore.createAccount({
      form: {
        locale,
        firstName: authUser.givenName,
        lastName: authUser.familyName,
        primaryResidenceSelected,
        phones: [placeholderPhone],
      },
      callback: ({ account, error }) => {
        if (account) {
          this.accountGlobalStore.setAccount(account);
          this.startFlowStore.invitationUsed();
          this.startFlowStore.accountCreated(account);
        }
        if (error) {
          this.router.navigate(
            [`/${CoreRoutes.root}/${CoreRoutes.noAccount}`],
            { queryParams }
          );
        }
      },
    });

    return true;
  }

  /**
   * Resolves a **valid** country code for the new account, or `null` when none can
   * be determined. Sources, in priority order:
   * 1. Invitation country — derived from the invitation-hash locale.
   * 2. ES-01 default region country (from the client collection).
   *
   * A candidate is only accepted when it is non-empty **and** maps to a recognized
   * country (`Country.getCountryByCountryCode`). Returning `null` signals the caller
   * to fall back to the manual create-account view rather than auto-create with a
   * bad/empty country (e.g. collection not loaded or no default region configured).
   */
  private resolveEs01CountryCode(): string | null {
    const languages = this.dictionaryGlobalStore.languagesFiltered();
    const hash = this.ngrxStore.selectSignal(authInvitationHashSelector)();

    const candidates: (string | undefined)[] = [];
    if (hash?.locale?.length > 0) {
      candidates.push(
        Language.getLocaleByLocaleId(languages, hash.locale)?.countryCode
      );
    }
    candidates.push(
      this.es01ClientStore.regions().find(region => region.isdefault)
        ?.countrycode
    );

    for (const code of candidates) {
      if (code?.length > 0 && Country.getCountryByCountryCode(code)) {
        return code;
      }
    }

    return null;
  }

  /**
   * Resolves the locale for the new account. Uses the invitation-hash locale when
   * present; otherwise builds one from the browser language + the resolved ES-01
   * country. `languages` is guaranteed non-empty here (the base start flow routes
   * to the generic error view when the dictionary is empty).
   */
  private resolveEs01Locale(countryCode: string): Locale {
    const languages = this.dictionaryGlobalStore.languagesFiltered();
    const hash = this.ngrxStore.selectSignal(authInvitationHashSelector)();

    if (hash?.locale?.length > 0) {
      return Language.getLocaleByLocaleId(languages, hash.locale);
    }

    const browserLanguageCode = window.navigator.language.slice(0, 2);
    return Language.getLocaleByLocaleId(
      languages,
      `${browserLanguageCode}-${countryCode}`
    );
  }
}
